import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, fmtDate, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Check, Sparkles, CreditCard, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function Billing() {
    const { currentOrg, refreshOrgs } = useAuth();
    const [sub, setSub] = useState(null);
    const [plans, setPlans] = useState([]);
    const [yearly, setYearly] = useState(false);
    const [busy, setBusy] = useState(null);
    const [ppConfig, setPpConfig] = useState({ configured: false });
    const [params, setParams] = useSearchParams();

    const load = async () => {
        const [{ data: s }, { data: p }, { data: cfg }] = await Promise.all([
            api.get("/billing/subscription"),
            api.get("/billing/plans"),
            api.get("/billing/paypal/config"),
        ]);
        setSub(s); setPlans(p.plans); setPpConfig(cfg);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    // Surface return-from-PayPal outcomes
    useEffect(() => {
        const upgraded = params.get("upgraded");
        const cancelled = params.get("cancelled");
        const error = params.get("error");
        const status = params.get("status");
        if (upgraded) {
            toast.success(`Subscription active — you are on ${upgraded.toUpperCase()}`);
            refreshOrgs();
        } else if (cancelled) {
            toast.info("Checkout cancelled");
        } else if (error) {
            toast.error(`PayPal: ${error}`);
        } else if (status) {
            toast.info(`PayPal status: ${status}`);
        }
        if (upgraded || cancelled || error || status) {
            params.delete("upgraded"); params.delete("cancelled"); params.delete("error"); params.delete("status");
            setParams(params, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Deep-link from UpgradeNudge: ?select=pro|team — highlight the card, auto-scroll.
    const selectParam = params.get("select");
    useEffect(() => {
        if (!selectParam || !sub) return;
        // Give the plan grid one tick to render, then scroll/focus.
        const t = setTimeout(() => {
            const el = document.querySelector(`[data-testid='choose-plan-${selectParam}']`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.closest("div")?.classList.add("ring-2", "ring-secondary");
                setTimeout(() => el.closest("div")?.classList.remove("ring-2", "ring-secondary"), 3500);
            }
        }, 250);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sub, selectParam]);

    const change = async (plan) => {
        if (plan === sub?.plan) return;
        setBusy(plan);
        try {
            if (plan === "free") {
                if (sub?.plan !== "free" && !window.confirm("Downgrade to Free? This will cancel your PayPal subscription immediately.")) {
                    setBusy(null); return;
                }
                if (sub?.plan !== "free") {
                    await api.post("/billing/paypal/cancel-subscription").catch(() => {});
                }
                await api.post("/billing/change-plan", { plan, cycle: yearly ? "yearly" : "monthly" });
                toast.success("Downgraded to Free");
                await refreshOrgs(); load();
            } else if (ppConfig.configured) {
                const { data } = await api.post("/billing/paypal/subscription", { plan });
                toast.info("Redirecting to PayPal...");
                window.location.href = data.approve_url;
            } else {
                // Fallback: demo plan switch (PayPal not configured)
                await api.post("/billing/change-plan", { plan, cycle: yearly ? "yearly" : "monthly" });
                toast.success(`Switched to ${plan.toUpperCase()} plan`);
                await refreshOrgs(); load();
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    if (!sub) return null;

    return (
        <div>
            <PageHeader subtitle="Workspace" title="Subscription & billing" />

            <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <div className="rounded-3xl bg-deep-ocean text-white p-6 shadow-card lg:col-span-2 relative overflow-hidden">
                    <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-secondary/30 blur-3xl" />
                    <div className="relative">
                        <div className="text-xs uppercase tracking-wider text-white/60">Current plan</div>
                        <div className="font-heading text-5xl mt-2">{sub.plan.toUpperCase()}</div>
                        <div className="flex flex-wrap items-center gap-3 mt-4">
                            <Badge className="bg-white/10 text-white border-0">{sub.status}</Badge>
                            {sub.next_billing_date && <span className="text-xs text-white/70">Renews {fmtDate(sub.next_billing_date)} · {sub.cycle}</span>}
                        </div>
                        <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
                            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                                <div className="text-[11px] uppercase text-white/60 tracking-wider">Seats</div>
                                <div className="mt-1 font-heading text-2xl">{sub.seats_used} / {sub.seat_limit}</div>
                            </div>
                            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                                <div className="text-[11px] uppercase text-white/60 tracking-wider">Receipt OCR</div>
                                <div className="mt-1 font-heading text-2xl">{sub.features.ocr_per_month}/mo</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-6">
                    <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-secondary" /><h3 className="font-heading text-lg text-ocean-700">Billing events</h3></div>
                    <div className="mt-4 space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
                        {sub.events.map((e) => (
                            <div key={e.id} className="flex items-center justify-between text-xs py-1.5 border-b border-sand-200 last:border-0">
                                <div>
                                    <div className="text-ocean-700 font-medium">{e.event_type}</div>
                                    <div className="text-muted-foreground">{fmtDate(e.created_at)}</div>
                                </div>
                                {e.amount != null && <div className="font-mono">{fmtMoney(e.amount, "USD")}</div>}
                            </div>
                        ))}
                        {sub.events.length === 0 && <div className="text-sm text-muted-foreground py-4">No events yet.</div>}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between mb-5">
                <div>
                    <h3 className="font-heading text-2xl text-ocean-700">Change plan</h3>
                    <p className="text-sm text-muted-foreground">Switch anytime. Downgrade to Free keeps your data.</p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-white px-3 py-1.5">
                    <span className={`text-xs ${!yearly ? "text-ocean-700 font-medium" : "text-muted-foreground"}`}>Monthly</span>
                    <Switch checked={yearly} onCheckedChange={setYearly} data-testid="billing-cycle" />
                    <span className={`text-xs ${yearly ? "text-ocean-700 font-medium" : "text-muted-foreground"}`}>Yearly · save 17%</span>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
                {plans.map((p) => {
                    const price = yearly ? p.price_yearly : p.price_monthly;
                    const current = sub.plan === p.id;
                    return (
                        <div key={p.id} className={`rounded-3xl p-7 border shadow-card flex flex-col ${current ? "bg-ocean-50 border-secondary" : "bg-white border-sand-200"}`}>
                            <div className="flex items-center justify-between">
                                <h4 className="font-heading text-xl text-ocean-700">{p.name}</h4>
                                {current && <Badge className="bg-secondary text-white border-0">Current</Badge>}
                            </div>
                            <div className="mt-3 flex items-end gap-1">
                                <div className="font-heading text-4xl text-ocean-700">${price}</div>
                                <div className="text-sm text-muted-foreground mb-1.5">/{yearly ? "yr" : "mo"}</div>
                            </div>
                            <ul className="mt-5 space-y-2 text-sm flex-1">
                                {p.features.slice(0, 6).map((f) => (
                                    <li key={f} className="flex gap-2 text-ocean-700"><Check className="h-4 w-4 text-secondary mt-0.5 shrink-0" /> {f}</li>
                                ))}
                            </ul>
                            <Button
                                disabled={busy !== null || current}
                                onClick={() => change(p.id)}
                                className={`mt-6 rounded-xl ${current ? "bg-sand-200 text-ocean-700 hover:bg-sand-200" : "bg-ocean-700 hover:bg-ocean-700/90 text-white"}`}
                                data-testid={`choose-plan-${p.id}`}
                            >
                                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Current plan" : p.id === "free" ? "Downgrade" : "Upgrade"}
                            </Button>
                        </div>
                    );
                })}
            </div>
            <p className="text-xs text-muted-foreground mt-6 flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" />
                {ppConfig.configured
                    ? "Secure checkout powered by PayPal — your card details never touch our servers."
                    : "PayPal is not configured on this server — plan switches apply instantly for demo purposes."}
            </p>
        </div>
    );
}
